<template>
  <div v-if="canRender" class="refapp-video" :data-refapp-tagging-id="taggingId">
    <video
      ref="refappVideo"
      :autoplay="autoplay"
      :muted="muted"
      :loop="loop"
      class="refapp-video__media"
      :poster="poster"
      disablePictureInPicture
      playsinline
      data-refapp-tagging-target="video"
    >
      <source
        v-for="s in sources"
        :key="s.src"
        :src="s.src"
        :type="s.type"
        :media="getSourceMediaQuery(s.screenSize)"
      />
    </video>

    <slot
      name="controls"
      :muted="muted"
      :autoplay="autoplay"
      :controls="controls"
      :toggle-playback="togglePlayback"
      :toggle-audio="toggleAudio"
    />

    <slot name="content" />
  </div>
</template>

<script setup>
/**
 * HACK:
 * This empty script is required to make nested components work in Vue 3
 * while keeping compatibility with Vue 2 intact.
 */
</script>

<script>
const PLAYBACK_CLASS = 'refapp-video__controls-playback--paused';

// sm (360p) | md (720p) | lg (1080p+)
const mediaQueries = {
  sm: '(max-width:640px)',
  md: '(max-width:1280px)',
  lg: null,
};

export default {
  name: 'refapp-video-base',
  inject: ['sources', 'autoplay', 'muted', 'loop', 'poster', 'controls', 'taggingId'],
  data() {
    return {
      canRender: this.sources.some((src) => !!src),
    };
  },
  mounted() {
    const { refappVideo: video } = this.$refs;

    if (!video) return;

    const playbackControls = document.querySelector('.refapp-video__controls-playback');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            video.pause();
            playbackControls?.classList.add(PLAYBACK_CLASS);
            return;
          }

          if (this.autoplay) {
            video.play();
            playbackControls?.classList.remove(PLAYBACK_CLASS);
          }
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    this.observer = observer;

    observer.observe(video);
  },

  unmounted() {
    const { refappVideo: video } = this.$refs;
    video && this.observer.unobserve(video);
  },

  methods: {
    togglePlayback(button) {
      const { refappVideo: video } = this.$refs;
      const isPlaybackStopped = video.paused || video.stopped;

      const PLAYBACK_MODIFIER_CLASS = 'refapp-video__controls-playback--paused';
      button.classList[isPlaybackStopped ? 'remove' : 'add'](PLAYBACK_MODIFIER_CLASS);

      isPlaybackStopped ? video.play() : video.pause();
    },

    toggleAudio(button) {
      const { refappVideo: video } = this.$refs;

      const AUDIO_MODIFIER_CLASS = 'refapp-video__controls-audio--muted';
      button.classList[video.muted ? 'remove' : 'add'](AUDIO_MODIFIER_CLASS);

      video.muted = !video.muted;
    },

    getSourceMediaQuery(size) {
      // do not apply a mediaquery if the requested size is not interpretable
      if (!(size in mediaQueries)) return null;
      return mediaQueries[size];
    },
  },
};
</script>
